'use client';

import { useEffect, useRef, useState } from 'react';
import type { FiatTransactionStatus } from '@bts-web/data-layer/server';
import {
  getFiatTransaction,
  GetFiatTransactionResponse,
} from '../../../common/gqlActions';

// Temporary values for testing/integration. Spec calls for 2–5s interval;
const POLL_INTERVAL_MS = 5 * 1000;

const POLL_WINDOW_MS = 5 * 60 * 1000;

const TERMINAL_STATUSES: Array<FiatTransactionStatus> = [
  'CREDITED',
  'FINISHED',
  'REJECTED',
  'REFUNDED',
  'CANCELLED',
  'MANUAL_REVIEW',
];

export type TransferPollingStatus =
  | 'idle'
  | 'polling'
  | 'terminal'
  | 'timeout'
  | 'error';

interface UseTransferStatusPollingArgs {
  id: string;
  enabled: boolean;
  initialErrors?: string[];
}

export const useTransferStatusPolling = ({
  id,
  enabled,
  initialErrors,
}: UseTransferStatusPollingArgs) => {
  const hasInitialErrors = Boolean(initialErrors && initialErrors.length > 0);

  const [status, setStatus] = useState<TransferPollingStatus>(
    hasInitialErrors ? 'error' : 'idle',
  );

  const [errors, setErrors] = useState<string[] | null>(
    hasInitialErrors ? (initialErrors as string[]) : null,
  );

  const [transferStatus, setTransferStatus] =
    useState<FiatTransactionStatus>('INITIATED');

  const isMountedRef = useRef(true);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || !id || hasInitialErrors) return;

    isMountedRef.current = true;

    setStatus('polling');

    const startMs = Date.now();

    const tick = async () => {
      if (!isMountedRef.current || !enabled) return;

      let response: GetFiatTransactionResponse | null = null;

      try {
        response = await getFiatTransaction({ offerId: id });
      } catch {
        if (isMountedRef.current) setStatus('error');

        return;
      }

      if (!isMountedRef.current) return;

      if (response?.errors && response.errors.length > 0) {
        setStatus('error');

        setErrors(response?.errors);

        return;
      }

      const nextTransferStatus = response?.data?.status ?? null;

      if (nextTransferStatus) {
        setTransferStatus(nextTransferStatus);
      }

      if (
        nextTransferStatus &&
        TERMINAL_STATUSES.includes(nextTransferStatus)
      ) {
        setStatus('terminal');

        return;
      }

      if (Date.now() - startMs >= POLL_WINDOW_MS) {
        setStatus('timeout');

        return;
      }

      timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      isMountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);

        timeoutRef.current = null;
      }
    };
  }, [id, enabled, hasInitialErrors]);

  return { status, errors, transferStatus };
};
